import { Component, Input, OnInit, forwardRef, HostListener, ElementRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'app-dob-picker',
  templateUrl: './dob-picker.component.html',
  styleUrls: ['./dob-picker.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DobPickerComponent),
      multi: true
    }
  ]
})
export class DobPickerComponent implements OnInit, ControlValueAccessor {
  @Input() customClass: string = '';
  @Input() hasError: boolean = false;
  @Input() showIcon: boolean = false;
  @Input() maxDate: string = '';

  selectedDay: string = '';
  selectedMonth: string = '';
  selectedYear: string = '';

  showDayDropdown: boolean = false;
  showMonthDropdown: boolean = false;
  showYearDropdown: boolean = false;

  days: string[] = [];
  months = [
    { value: '01', name: 'Jan - 01' },
    { value: '02', name: 'Feb - 02' },
    { value: '03', name: 'Mar - 03' },
    { value: '04', name: 'Apr - 04' },
    { value: '05', name: 'May - 05' },
    { value: '06', name: 'Jun - 06' },
    { value: '07', name: 'Jul - 07' },
    { value: '08', name: 'Aug - 08' },
    { value: '09', name: 'Sep - 09' },
    { value: '10', name: 'Oct - 10' },
    { value: '11', name: 'Nov - 11' },
    { value: '12', name: 'Dec - 12' }
  ];
  years: number[] = [];

  disabled: boolean = false;
  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private eRef: ElementRef) {}

  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event) {
    if (!this.eRef.nativeElement.contains(event.target)) {
      this.closeAllDropdowns();
    }
  }

  ngOnInit() {
    this.generateYears();
    this.updateDays();
  }

  generateYears() {
    const maxAllowedYear = new Date().getFullYear() - 18;
    const minYear = 1950;
    this.years = [];
    for (let y = maxAllowedYear; y >= minYear; y--) {
      this.years.push(y);
    }
  }

  get availableMonths() {
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;
    if (this.selectedYear && parseInt(this.selectedYear, 10) === currentYear) {
      return this.months.filter(m => parseInt(m.value, 10) <= currentMonth);
    }
    return this.months;
  }

  getMonthName(val: string): string {
    if (!val) return '';
    const found = this.months.find(m => m.value === val);
    return found ? found.name.split(' ')[0] : val;
  }

  isUnderAge(): boolean {
    if (!this.selectedDay || !this.selectedMonth || !this.selectedYear) return false;
    const birthDate = new Date(`${this.selectedYear}-${this.selectedMonth}-${this.selectedDay}`);
    if (isNaN(birthDate.getTime())) return false;
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const mDiff = today.getMonth() - birthDate.getMonth();
    if (mDiff < 0 || (mDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age < 18;
  }

  updateDays() {
    const yearNum = this.selectedYear ? parseInt(this.selectedYear, 10) : new Date().getFullYear();
    const monthNum = this.selectedMonth ? parseInt(this.selectedMonth, 10) : 1;
    const totalDays = new Date(yearNum, monthNum, 0).getDate();
    
    let maxAllowedDay = totalDays;
    const now = new Date();
    if (yearNum === now.getFullYear() && monthNum === (now.getMonth() + 1)) {
      maxAllowedDay = Math.min(maxAllowedDay, now.getDate());
    }

    this.days = [];
    for (let d = 1; d <= maxAllowedDay; d++) {
      this.days.push(d < 10 ? '0' + d : '' + d);
    }

    if (this.selectedDay && parseInt(this.selectedDay, 10) > maxAllowedDay) {
      this.selectedDay = maxAllowedDay < 10 ? '0' + maxAllowedDay : '' + maxAllowedDay;
    }
  }

  toggleDayDropdown(event: Event) {
    event.stopPropagation();
    if (this.disabled) return;
    this.showMonthDropdown = false;
    this.showYearDropdown = false;
    this.showDayDropdown = !this.showDayDropdown;
  }

  toggleMonthDropdown(event: Event) {
    event.stopPropagation();
    if (this.disabled) return;
    this.showDayDropdown = false;
    this.showYearDropdown = false;
    this.showMonthDropdown = !this.showMonthDropdown;
  }

  toggleYearDropdown(event: Event) {
    event.stopPropagation();
    if (this.disabled) return;
    this.showDayDropdown = false;
    this.showMonthDropdown = false;
    this.showYearDropdown = !this.showYearDropdown;
  }

  closeAllDropdowns() {
    this.showDayDropdown = false;
    this.showMonthDropdown = false;
    this.showYearDropdown = false;
  }

  selectDay(d: string, event: Event) {
    event.stopPropagation();
    this.selectedDay = d;
    this.showDayDropdown = false;
    this.onDateChange();
  }

  selectMonth(m: string, event: Event) {
    event.stopPropagation();
    this.selectedMonth = m;
    this.showMonthDropdown = false;
    this.onDateChange();
  }

  selectYear(y: number | string, event: Event) {
    event.stopPropagation();
    this.selectedYear = '' + y;
    this.showYearDropdown = false;
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;
    if (this.selectedYear && parseInt(this.selectedYear, 10) === currentYear) {
      if (this.selectedMonth && parseInt(this.selectedMonth, 10) > currentMonth) {
        this.selectedMonth = currentMonth < 10 ? '0' + currentMonth : '' + currentMonth;
      }
    }
    this.onDateChange();
  }

  onDateChange() {
    this.updateDays();
    this.onTouched();
    if (this.selectedDay && this.selectedMonth && this.selectedYear) {
      const formattedDate = `${this.selectedYear}-${this.selectedMonth}-${this.selectedDay}`;
      
      const selectedDateObj = new Date(formattedDate);
      const today = new Date();
      today.setHours(23, 59, 59, 999);
      if (selectedDateObj > today) {
        this.onChange('');
        return;
      }

      this.onChange(formattedDate);
    } else {
      this.onChange('');
    }
  }

  writeValue(value: any): void {
    if (value && typeof value === 'string') {
      const parts = value.split('T')[0].split('-');
      if (parts.length === 3) {
        this.selectedYear = parts[0];
        this.selectedMonth = parts[1];
        this.selectedDay = parts[2];
        this.updateDays();
        return;
      } else {
        const d = new Date(value);
        if (!isNaN(d.getTime())) {
          this.selectedYear = '' + d.getFullYear();
          const m = d.getMonth() + 1;
          this.selectedMonth = m < 10 ? '0' + m : '' + m;
          const day = d.getDate();
          this.selectedDay = day < 10 ? '0' + day : '' + day;
          this.updateDays();
          return;
        }
      }
    }
    this.selectedDay = '';
    this.selectedMonth = '';
    this.selectedYear = '';
    this.updateDays();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
